import { useCallback, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconQuestionMarkFilled } from '@iwoca/icons';
import { Pagination, Table, Tooltip } from '@iwoca/orion';
import { ColumnDef } from '@iwoca/orion/lib/components/table/Table';
import { useSearchParams } from 'react-router';

import { CustomersMobileTable } from './CustomersMobileTable';
import { useGetSellerCustomers } from '../../../../api/lending/lapiHooks';
import { LoadingModal } from '../../../../components/LoadingModal/LoadingModal';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { formatDate } from '../../../Fees/RateContextHeader/RateContextHeader';
import { TCustomersTableColumns, TSelectedBuyer } from '../../Customers.types';
import { CustomerInformationModal } from '../CustomerInformationModal/CustomerInformationModal';
import { CustomersStatusModal } from '../CustomersStatusModal/CustomersStatusModal';

export const CustomersTable = ({
  currentPage,
  setCurrentPage,
  searchQuery,
}: {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  searchQuery?: string;
}) => {
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const [showCustomerModal, setShowCustomerModal] = useState<boolean>(false);
  const [selectedBuyer, setSelectedBuyer] = useState<TSelectedBuyer>({
    stateKey: '',
    name: '',
    applicationStage: undefined,
    applicationSubStages: undefined,
    limitAmount: null,
    limitDuration: null,
  });
  const [hasFiredTooltipEvent, setHasFiredTooltipEvent] = useState(false);
  const fireTooltipEvent = useCallback(() => {
    if (hasFiredTooltipEvent) return;
    tracking.ampli.customersPageLeftToSpendTooltipHovered();
    setHasFiredTooltipEvent(true);
  }, [hasFiredTooltipEvent]);
  const [searchParams] = useSearchParams();
  const { isMobile } = useIsMobile({ maxWidth: 1024 });

  const { sellerCustomers, metadata, loadingSellerCustomers } =
    useGetSellerCustomers({
      page: currentPage,
      spending_limit_status: searchParams.get('status') || undefined,
      application_stage: searchParams.get('stage') || undefined,
      search: searchQuery,
    });

  const handleClick = (
    row: TCustomersTableColumns,
    button: 'application_stage' | 'company_name',
  ) => {
    if (button === 'application_stage') {
      tracking.ampli.customersPageApplicationStageClicked();
    }
    if (button === 'company_name') {
      tracking.ampli.customersPageBuyerCompanyNameClicked();
    }

    setSelectedBuyer({
      stateKey: row.buyer_state_key,
      name: row.company_name || 'Awaiting details',
      applicationStage: row.application_stage,
      applicationSubStages: row.application_sub_stage_object,
      // TODO(types): fix in schema / add null checks.
      limitAmount: row.limit_amount!,
      limitDuration: row.limit_duration!,
    });
    setShowCustomerModal((prev) => !prev);
  };

  const getDaysSinceExpiry = (timeout: string | null) => {
    if (timeout === null) return null;

    const days = Math.ceil(
      (Date.now() - new Date(timeout).getTime()) / (1000 * 60 * 60 * 24) - 1,
    );

    return days;
  };

  const columns: ColumnDef<TCustomersTableColumns>[] = [
    {
      cell: (info) =>
        formatDate(info.row.original.earliest_interacted_date, 'short'),
      id: 'date',
      header: 'Date',
      className: '!text-s font-med text-primary-10 !items-center w-1/5',
    },
    {
      cell: (info) => {
        if (shouldDisableModalClick(info.row.original, 'company_name'))
          return <div>{info.row.original.company_name}</div>;

        return (
          <button
            className="cursor-pointer border-none bg-transparent px-0 text-left text-s text-primary-40 underline"
            onClick={() => handleClick(info.row.original, 'company_name')}
          >
            {info.row.original.company_name}
          </button>
        );
      },
      id: 'companyName',
      header: 'Company Name',
      className: '!text-s font-med text-primary-10 !items-center w-1/5',
    },
    {
      cell: (info) => {
        if (shouldDisableModalClick(info.row.original, 'application_stage'))
          return (
            <div
              className={`text-s ${
                info.row.original.spending_limit_status.value ===
                  'last_approved_offer' &&
                info.row.original.limit_amount !== null &&
                'text-structure-60'
              }`}
            >
              {info.row.original.application_stage.label}
            </div>
          );

        return (
          <button
            className="cursor-pointer border-none bg-transparent px-0 text-s text-primary-40 underline"
            onClick={() => handleClick(info.row.original, 'application_stage')}
          >
            {info.row.original.application_stage.label}
          </button>
        );
      },
      id: 'applicationStage',
      header: 'Application Stage',
      className: '!text-s font-med text-primary-10 !items-center w-1/5',
    },
    {
      cell: (info) => info.row.original.spending_limit_status.label,
      id: 'limitStatus',
      header: () => (
        <>
          <div className="flex-shrink-0">Status</div>
          <button
            className="flex-shrink-0 cursor-pointer border-none bg-transparent pl-m text-xs text-primary-40 underline"
            onClick={() => setShowStatusModal(!showStatusModal)}
          >
            What are the statuses?
          </button>
        </>
      ),
      className:
        '!text-s font-med text-primary-10 !items-center w-1/5 min-w-[222px]',
    },
    {
      cell: (info) => {
        const { showTooltip, text: tooltipText } = handleTooltip(
          info.row.original,
        );
        const daysSinceExpiry = getDaysSinceExpiry(
          info.row.original.limit_timeout,
        );
        const showDaysSinceExpiry =
          info.row.original.spending_limit_status.value ===
            'last_approved_offer' &&
          daysSinceExpiry !== null &&
          daysSinceExpiry > 0;

        if (
          info.row.original.limit_amount === null ||
          info.row.original.spending_limit_status.value === 'declined_offer'
        )
          return (
            <div className="flex gap-l text-structure-80">
              ----------{' '}
              {showTooltip && (
                <div
                  className="flex h-[20px] w-[20px] items-center rounded-l border border-primary-40"
                  data-testid="tooltip-trigger"
                  onMouseEnter={fireTooltipEvent}
                  onFocus={fireTooltipEvent}
                >
                  <Tooltip
                    className="text-primary-10"
                    content={tooltipText}
                    placement={info.row.index >= 3 ? 'top' : 'bottom'}
                  >
                    <IconQuestionMarkFilled
                      fill="primary-10"
                      height="18"
                      width="18"
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          );

        return (
          <div className="flex w-full flex-wrap justify-between gap-s">
            <div
              className={`${info.row.original.spending_limit_status.value === 'offer_approved' && 'text-success-mid'}`}
            >
              {displayCurrency(info.row.original.limit_amount)}
            </div>
            {!isMobile && (
              <div className="font-reg text-structure-60">
                {showDaysSinceExpiry &&
                  (daysSinceExpiry < 365
                    ? `Exp ${daysSinceExpiry}d ago`
                    : 'Exp a year+ ago')}
              </div>
            )}
          </div>
        );
      },
      id: 'limitAmount',
      header: 'Left to spend',
      className: '!text-s font-med text-primary-10 !items-center w-1/5',
    },
  ];

  return (
    <div className="w-full">
      <LoadingModal
        key={currentPage}
        isOpen={loadingSellerCustomers}
        title="We’re pulling together the latest customer updates"
        subText="You’ve got a lot of great data here - give us a moment while we bring it all together."
      />
      <CustomerInformationModal
        open={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        selectedBuyer={selectedBuyer}
      />
      <CustomersStatusModal
        open={showStatusModal}
        close={() => setShowStatusModal(false)}
      />
      {isMobile ? (
        <CustomersMobileTable
          sellerCustomers={sellerCustomers || []}
          loading={loadingSellerCustomers}
          handleClick={handleClick}
          openStatusModal={() => setShowStatusModal(true)}
        />
      ) : (
        <Table
          data={sellerCustomers || []}
          columns={columns}
          loading={loadingSellerCustomers}
          className="[&_td]:px-l [&_td]:py-r [&_thead_th:nth-child(4)]:min-w-[222px] [&_thead_th]:w-1/5"
        />
      )}

      {metadata?.total_pages && metadata.total_pages > 1 && (
        <Pagination
          className="mx-auto mt-2xl"
          page={currentPage}
          count={metadata?.total_pages}
          onPageChange={(currentPage) => {
            document.body.scrollTop = document.documentElement.scrollTop = 0;
            setCurrentPage(currentPage);
          }}
        />
      )}
    </div>
  );
};

export const shouldDisableModalClick = (
  row: TCustomersTableColumns,
  button: 'application_stage' | 'company_name',
) => {
  // disable for x-sell customers
  if (row.application_stage.value === 'complete' && row.limit_amount === null) {
    return true;
  }

  // disable for declined customers
  if (row.application_stage.value === 'declined') {
    return true;
  }

  if (button === 'company_name') return false;

  // disable for expired spending limit customers
  if (row.spending_limit_status.value === 'last_approved_offer') {
    return true;
  }

  return false;
};

const handleTooltip = (row: TCustomersTableColumns) => {
  // x-sell customers
  if (row.application_stage.value === 'complete' && row.limit_amount === null) {
    return {
      showTooltip: true,
      text: 'Currently unable to estimate this customers Spending Limit',
    };
  }

  // customers who haven't finished the application process
  if (['business_details', 'security'].includes(row.application_stage.value)) {
    return {
      showTooltip: true,
      text: 'A spending limit hasn’t been calculated yet.',
    };
  }
  return { showTooltip: false, text: '' };
};
